import { Fragment } from "react";
import { Check, Eye, Info, Minus } from "lucide-react";

type StaffRole =
  | "ADMIN"
  | "ADMINISTRATIVO"
  | "VENDEDOR"
  | "PLANEJADOR"
  | "COMPRADOR"
  | "CONFERENTE"
  | "PRODUCAO"
  | "EXPEDICAO"
  | "SEPARACAO"
  | "ENTREGADOR";

const ROLE_ORDER: StaffRole[] = [
  "ADMIN",
  "ADMINISTRATIVO",
  "VENDEDOR",
  "PLANEJADOR",
  "COMPRADOR",
  "CONFERENTE",
  "PRODUCAO",
  "EXPEDICAO",
  "SEPARACAO",
  "ENTREGADOR",
];

const ROLE_LABELS: Record<StaffRole, string> = {
  ADMIN: "Admin",
  ADMINISTRATIVO: "Administrativo",
  VENDEDOR: "Vendedor",
  PLANEJADOR: "Planejador",
  COMPRADOR: "Comprador",
  CONFERENTE: "Conferente",
  PRODUCAO: "Produção",
  EXPEDICAO: "Expedição",
  SEPARACAO: "Separação IN NATURA",
  ENTREGADOR: "Entregador",
};

interface ModuleRow {
  label: string;
  path: string;
  description?: string;
  roles: StaffRole[] | "all-staff";
  /** Papéis que herdam acesso ao módulo mas em modo somente-leitura (ex.: ADMINISTRATIVO em Funcionários). */
  readOnlyRoles?: StaffRole[];
}

// SEPARACAO tem acesso deliberadamente restrito (só produção/expedição em modo visualização,
// estoque em modo visualização e relatório de produção in natura) — não entra no grupo "all-staff".
const ALL_STAFF: StaffRole[] = ROLE_ORDER.filter((role) => role !== "ENTREGADOR" && role !== "SEPARACAO");

const MODULES: { group: string; items: ModuleRow[] }[] = [
  {
    group: "Operação comercial",
    items: [
      { label: "Dashboard / Operações", path: "/admin/operacoes", roles: "all-staff" },
      { label: "Clientes (consulta)", path: "/admin/clientes", roles: "all-staff", description: "Todos os papéis consultam; apenas ADMIN valida e PLANEJADOR/ADMIN editam perfil" },
      { label: "Novo pedido manual", path: "/admin/pedidos/novo", roles: ["ADMIN", "PLANEJADOR"] },
      { label: "Unidades de entrega", path: "/admin/unidades-entrega", roles: ["ADMIN", "PLANEJADOR", "VENDEDOR", "COMPRADOR"], description: "VENDEDOR e COMPRADOR só consultam; escrita em ADMIN/PLANEJADOR" },
    ],
  },
  {
    group: "Pedido — operação por etapa",
    items: [
      { label: "Avançar EM_COMPRA → EM_PRODUCAO + lista de compras", path: "Compras", roles: ["ADMIN", "PLANEJADOR", "COMPRADOR"] },
      { label: "Avançar EM_PRODUCAO → EM_EXPEDICAO + lista de produção", path: "Produção", roles: ["ADMIN", "PLANEJADOR", "PRODUCAO"] },
      { label: "Visualizar dashboard de produção (sem avançar etapa)", path: "/dashboard/producao", roles: ["SEPARACAO"], description: "Só visualização — a etapa avança via ADMIN/PLANEJADOR/PRODUCAO" },
      { label: "Alterar status de itens In Natura na produção (Pronto/Parcial/Sem matéria-prima)", path: "/dashboard/producao", roles: ["SEPARACAO"], description: "Restrito a itens in natura; itens processados continuam exclusivos de ADMIN/PLANEJADOR/PRODUCAO" },
      { label: "Avançar EM_EXPEDICAO → EM_ROTA + lista de expedição", path: "Expedição", roles: ["ADMIN", "PLANEJADOR", "CONFERENTE", "EXPEDICAO"] },
      { label: "Visualizar dashboard de expedição (sem alterar)", path: "/dashboard/expedicao", roles: ["SEPARACAO"], description: "Só visualização — sem abrir conferência" },
      { label: "Alterar itens do pedido (quantidade + unidade + corte + cancelar)", path: "Alteração total", roles: ["ADMIN", "PLANEJADOR", "CONFERENTE", "EXPEDICAO"] },
      { label: "Alterar apenas quantidade do item", path: "Alteração parcial", roles: ["ADMIN", "PLANEJADOR", "PRODUCAO", "CONFERENTE", "EXPEDICAO"] },
    ],
  },
  {
    group: "Catálogo e preços",
    items: [
      { label: "Catálogo de produtos", path: "/admin/produtos", roles: ["ADMIN", "PLANEJADOR"] },
      { label: "Tabelas de preço", path: "/admin/tabelas", roles: ["ADMIN", "PLANEJADOR"] },
      { label: "Fornecedores", path: "/admin/fornecedores", roles: ["ADMIN", "PLANEJADOR", "CONFERENTE", "EXPEDICAO"] },
    ],
  },
  {
    group: "Suprimentos",
    items: [
      { label: "Estoque", path: "/admin/estoque", roles: ["ADMIN", "PLANEJADOR", "COMPRADOR", "CONFERENTE", "EXPEDICAO", "PRODUCAO", "SEPARACAO"], description: "PRODUCAO e SEPARACAO apenas consultam; CONFERENTE/EXPEDICAO ajustam saldo" },
      { label: "Recebimentos (entrada de mercadorias)", path: "/admin/recebimentos", roles: ["ADMIN", "PLANEJADOR", "CONFERENTE", "EXPEDICAO"] },
      { label: "Lista de compras", path: "/admin/relatorios/lista-de-compras", roles: ["ADMIN", "PLANEJADOR", "COMPRADOR", "CONFERENTE", "EXPEDICAO"] },
      { label: "Relatório de recebimentos", path: "/admin/relatorios/recebimentos", roles: ["ADMIN", "PLANEJADOR", "CONFERENTE", "EXPEDICAO"], description: "Sem valor — recebimento não tem custo cadastrado" },
    ],
  },
  {
    group: "Produção e entrega",
    items: [
      { label: "Relatório de produção", path: "/admin/relatorios/producao", roles: ["ADMIN", "PLANEJADOR", "PRODUCAO", "SEPARACAO"], description: "SEPARACAO só vê e imprime itens In Natura, por cliente" },
      { label: "Entregas", path: "/admin/entregas", roles: ["ADMIN", "PLANEJADOR", "CONFERENTE", "EXPEDICAO"] },
      { label: "App do entregador (apenas suas entregas)", path: "/entregador", roles: ["ADMIN", "ENTREGADOR"] },
    ],
  },
  {
    group: "Relatórios e configurações",
    items: [
      { label: "Relatório de vendas", path: "/admin/relatorios/vendas", roles: ["ADMIN", "PLANEJADOR", "VENDEDOR"] },
      { label: "Relatório de pedidos (entregues/cancelados/alterados)", path: "/admin/relatorios/pedidos", roles: ["ADMIN", "PLANEJADOR", "VENDEDOR"] },
      { label: "Funcionários", path: "/admin/funcionarios", roles: ["ADMIN"], readOnlyRoles: ["ADMINISTRATIVO"], description: "Criar, editar e desativar usuários staff — Administrativo só visualiza (não gerencia cargos)" },
      { label: "Configurações de entrega", path: "/admin/configuracoes/entregas", roles: ["ADMIN"] },
    ],
  },
];

// ADMINISTRATIVO herda automaticamente qualquer módulo liberado para ADMIN -- mesma equivalência do
// backend (AuthorizationService) e do roteamento (RequireRole/AdminLayout).
function hasAccess(row: ModuleRow, role: StaffRole): boolean {
  if (row.roles === "all-staff") return ALL_STAFF.includes(role);
  return row.roles.includes(role) || (role === "ADMINISTRATIVO" && row.roles.includes("ADMIN"));
}

function isReadOnlyAccess(row: ModuleRow, role: StaffRole): boolean {
  return !!row.readOnlyRoles?.includes(role);
}

export default function AdminPermissions() {
  return (
    <div className="container max-w-6xl space-y-6 py-8">
      <header className="space-y-2">
        <h1 className="font-display text-3xl font-bold tracking-tight">Permissões por papel</h1>
        <p className="text-muted-foreground">
          Mapa de acesso a cada módulo do sistema por papel. Os papéis são atribuídos em{" "}
          <a href="/admin/funcionarios" className="underline underline-offset-2 hover:text-foreground">
            Funcionários
          </a>
          .
        </p>
      </header>

      <div className="surface-card flex items-start gap-2 border-info/30 bg-info/5 p-3 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" />
        <p className="text-muted-foreground">
          Esta tela é apenas para consulta. Para alterar o papel de um usuário, vá em <strong>Funcionários</strong>.
          Para adicionar ou remover módulos do escopo de um papel, é necessário ajustar o backend.
        </p>
      </div>

      <div className="surface-card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                <th className="sticky left-0 z-10 min-w-[16rem] bg-secondary/40 px-4 py-3 text-left font-display font-semibold">
                  Módulo
                </th>
                {ROLE_ORDER.map((role) => (
                  <th
                    key={role}
                    className="min-w-[6rem] border-l border-border/60 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {ROLE_LABELS[role]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MODULES.map((group) => (
                <Fragment key={group.group}>
                  <tr className="border-b border-border bg-secondary/20">
                    <td
                      colSpan={ROLE_ORDER.length + 1}
                      className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                      {group.group}
                    </td>
                  </tr>
                  {group.items.map((row) => (
                    <tr key={row.path} className="border-b border-border/60 hover:bg-secondary/20">
                      <td className="sticky left-0 z-10 bg-background px-4 py-3">
                        <div className="font-medium">{row.label}</div>
                        {row.description && (
                          <div className="mt-0.5 text-xs text-muted-foreground">{row.description}</div>
                        )}
                        <div className="mt-0.5 text-[10px] font-mono text-muted-foreground/70">{row.path}</div>
                      </td>
                      {ROLE_ORDER.map((role) => {
                        const access = hasAccess(row, role);
                        const readOnly = access && isReadOnlyAccess(row, role);
                        return (
                          <td
                            key={role}
                            className="border-l border-border/60 px-3 py-3 text-center align-middle"
                          >
                            {readOnly ? (
                              <Eye className="mx-auto h-4 w-4 text-info" aria-label={`${ROLE_LABELS[role]} tem acesso somente leitura`} />
                            ) : access ? (
                              <Check className="mx-auto h-4 w-4 text-success" aria-label={`${ROLE_LABELS[role]} tem acesso`} />
                            ) : (
                              <Minus
                                className="mx-auto h-4 w-4 text-muted-foreground/30"
                                aria-label={`${ROLE_LABELS[role]} sem acesso`}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Legend icon={Check} iconClass="text-success" label="Tem acesso ao módulo" />
        <Legend icon={Eye} iconClass="text-info" label="Acesso somente leitura" />
        <Legend icon={Minus} iconClass="text-muted-foreground/40" label="Sem acesso" />
      </div>
    </div>
  );
}

function Legend({
  icon: Icon,
  iconClass,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  iconClass: string;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-xs">
      <Icon className={`h-4 w-4 ${iconClass}`} />
      <span>{label}</span>
    </div>
  );
}
